(() => {
  const SELECTOR = '[data-nfc-product-root]';

  function runSafely(name, callback) {
    try {
      return callback();
    } catch (error) {
      console.error(`[Designly NFC] ${name} failed`, error);
      return null;
    }
  }

  function initSection(root) {
    if (!root || root.dataset.nfcReady === 'true') return;
    root.dataset.nfcReady = 'initializing';

    const form = root.querySelector('.lnf__product-form');
    const addButton = root.querySelector('[data-add-button]');
    const addLabel = root.querySelector('[data-add-label]');
    const stickyAdd = root.querySelector('[data-sticky-add]');
    const stickyLabel = stickyAdd?.querySelector('span');
    const toast = root.querySelector('[data-cart-toast]');
    const toastMessage = root.querySelector('[data-cart-message]');
    const stickyBar = root.querySelector('[data-sticky-bar]');
    const productInfo = root.querySelector('.lnf__product-info');

    runSafely('reveal effects', () => initReveals(root));
    runSafely('product gallery', () => initGallery(root));
    runSafely('interactive NFC demo', () => initInteractiveDemo(root));
    runSafely('customer videos', () => initUgcVideos(root));

    const updateSelection = runSafely('platform picker', () => initPlatformPicker(root, {
      addButton,
      addLabel,
      stickyAdd,
      stickyLabel,
    }));

    root.dataset.nfcReady = 'true';

    root.querySelector('[data-toast-close]')?.addEventListener('click', () => {
      if (toast) toast.hidden = true;
    });

    if (stickyBar && productInfo && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.bottom < 0);
      }, { threshold: 0.04 });
      observer.observe(productInfo);
    } else if (stickyBar) {
      stickyBar.classList.add('is-visible');
    }

    form?.addEventListener('submit', async (event) => {
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'add') return;
      event.preventDefault();

      const selectedItems = collectSelectedPlatforms(root);
      if (!selectedItems.length) {
        showToast(toast, toastMessage, 'اختر منصة واحدة على الأقل قبل الإضافة.');
        return;
      }

      setButtonsBusy(addButton, stickyAdd, true);
      if (addLabel) addLabel.textContent = `جارٍ إضافة ${selectedItems.length} اختيار…`;
      if (stickyLabel) stickyLabel.textContent = 'جارٍ الإضافة…';

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const items = selectedItems.map((item) => {
          const properties = { 'المنصة المختارة': item.platformName };
          if (item.link) properties['الرابط المراد ربطه'] = item.link;
          return {
            id: item.variantId,
            quantity: item.quantity,
            properties,
          };
        });

        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        });
        const added = await response.json();
        if (!response.ok) throw new Error(added.description || added.message || 'تعذرت إضافة الاختيارات');

        const cartResponse = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        updateThemeCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, items: added.items || added } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart, items: added.items || added } }));
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, items: added.items || added } }));

        const totalTags = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
        showToast(toast, toastMessage, `تمت إضافة ${totalTags} تاج عبر ${selectedItems.length} منصة إلى السلة.`);
      } catch (error) {
        showToast(toast, toastMessage, error.message || 'حصل خطأ. حاول مرة أخرى.');
      } finally {
        setButtonsBusy(addButton, stickyAdd, false);
        updateSelection?.();
      }
    });
  }

  function initGallery(root) {
    root.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mainImage = root.querySelector('.lnf__main-image');
        if (!mainImage) return;
        root.querySelectorAll('[data-gallery-thumb]').forEach((item) => item.classList.remove('is-active'));
        thumb.classList.add('is-active');
        mainImage.style.opacity = '0';
        window.setTimeout(() => {
          mainImage.src = thumb.dataset.imageSrc || mainImage.src;
          mainImage.srcset = '';
          mainImage.alt = thumb.dataset.imageAlt || '';
          mainImage.style.opacity = '1';
        }, 140);
      });
    });
  }

  function initPlatformPicker(root, buttons) {
    const items = [...root.querySelectorAll('[data-platform-item]')];
    const countNode = root.querySelector('[data-selected-count]');
    const totalNode = root.querySelector('[data-selection-total]');
    const mainPrice = root.querySelector('[data-main-price]');
    const stickyPrice = root.querySelector('[data-sticky-price]');

    function updateSelection() {
      const selected = collectSelectedPlatforms(root);
      const totalCents = selected.reduce((sum, item) => sum + (item.priceCents * item.quantity), 0);
      const formattedTotal = formatMoney(totalCents, root);

      if (countNode) countNode.textContent = String(selected.length);
      if (totalNode) totalNode.textContent = formattedTotal;
      if (mainPrice) mainPrice.textContent = formattedTotal;
      if (stickyPrice) stickyPrice.textContent = formattedTotal;

      const available = selected.length > 0;
      [buttons.addButton, buttons.stickyAdd].forEach((button) => {
        if (button) button.disabled = !available;
      });
      if (buttons.addLabel) buttons.addLabel.textContent = available ? 'أضف اختياراتك للسلة' : 'اختر منصة أولًا';
      if (buttons.stickyLabel) buttons.stickyLabel.textContent = available ? 'أضف اختياراتك' : 'اختر منصة';
    }

    items.forEach((item) => {
      const checkbox = item.querySelector('[data-platform-checkbox]');
      const config = item.querySelector('[data-platform-config]');
      const link = item.querySelector('[data-platform-link]');
      const quantity = item.querySelector('[data-platform-qty]');

      checkbox?.addEventListener('change', () => {
        const selected = checkbox.checked && !checkbox.disabled;
        item.classList.toggle('is-selected', selected);
        if (config) config.hidden = !selected;
        if (link) link.disabled = !selected;
        updateSelection();
      });

      item.querySelector('[data-platform-qty-minus]')?.addEventListener('click', () => {
        if (!quantity) return;
        quantity.value = String(Math.max(1, Number(quantity.value || 1) - 1));
        updateSelection();
      });
      item.querySelector('[data-platform-qty-plus]')?.addEventListener('click', () => {
        if (!quantity) return;
        quantity.value = String(Math.max(1, Number(quantity.value || 1) + 1));
        updateSelection();
      });
      quantity?.addEventListener('change', () => {
        quantity.value = String(Math.max(1, Math.floor(Number(quantity.value || 1))));
        updateSelection();
      });
    });

    updateSelection();
    return updateSelection;
  }

  function collectSelectedPlatforms(root) {
    return [...root.querySelectorAll('[data-platform-item]')]
      .filter((item) => item.querySelector('[data-platform-checkbox]')?.checked)
      .map((item) => ({
        variantId: item.querySelector('[data-platform-checkbox]')?.value,
        platformName: item.dataset.platformName || 'NFC',
        priceCents: Number(item.dataset.priceCents || 0),
        quantity: Math.max(1, Math.floor(Number(item.querySelector('[data-platform-qty]')?.value || 1))),
        link: item.querySelector('[data-platform-link]')?.value.trim() || '',
      }))
      .filter((item) => item.variantId);
  }

  function formatMoney(cents, root) {
    const currency = window.Shopify?.currency?.active || root.dataset.currency || 'SAR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ar-SA', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  function initInteractiveDemo(root) {
    const demo = root.querySelector('[data-nfc-demo]');
    if (!demo) return;

    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        playInteractiveDemo(demo);
        observer.unobserve(demo);
      }, { threshold: 0.35 });
      observer.observe(demo);
    } else {
      playInteractiveDemo(demo);
    }
  }

  function playInteractiveDemo(demo) {
    if (!demo) return;
    const status = demo.querySelector('[data-demo-status]');
    const timers = Array.isArray(demo._lnfDemoTimers) ? demo._lnfDemoTimers : [];
    timers.forEach(window.clearTimeout);
    demo._lnfDemoTimers = [];
    demo.classList.remove('is-playing');
    void demo.offsetWidth;
    demo.classList.add('is-playing');
    if (status) status.textContent = 'قرّب التاج من أعلى الهاتف';
    demo._lnfDemoTimers.push(window.setTimeout(() => { if (status) status.textContent = 'الهاتف يتعرّف على تاج NFC…'; }, 1450));
    demo._lnfDemoTimers.push(window.setTimeout(() => { if (status) status.textContent = 'ظهر الإشعار — الرابط جاهز للفتح'; }, 2550));
    demo._lnfDemoTimers.push(window.setTimeout(() => { if (status) status.textContent = 'تمت التجربة في ثوانٍ. أعدها متى شئت.'; }, 4550));
  }

  function handleDelegatedClick(event) {
    const replay = event.target.closest?.('[data-demo-replay]');
    if (!replay) return;
    const demo = replay.closest('[data-nfc-demo]');
    playInteractiveDemo(demo);
  }

  function initUgcVideos(root) {
    const videos = [...root.querySelectorAll('.lnf__ugc video')];
    if (!videos.length) return;

    videos.forEach((video) => {
      video.setAttribute('playsinline', '');
      video.addEventListener('play', () => {
        videos.forEach((otherVideo) => {
          if (otherVideo !== video && !otherVideo.paused) otherVideo.pause();
        });
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && !entry.target.paused) entry.target.pause();
        });
      }, { threshold: 0.2 });
      videos.forEach((video) => observer.observe(video));
    }
  }

  function setButtonsBusy(addButton, stickyAdd, busy) {
    [addButton, stickyAdd].forEach((button) => {
      if (button) button.disabled = busy;
    });
    addButton?.classList.toggle('is-loading', busy);
  }

  function showToast(toast, messageNode, message) {
    if (messageNode) messageNode.textContent = message;
    if (!toast) return;
    toast.hidden = false;
    window.clearTimeout(toast._hideTimer);
    toast._hideTimer = window.setTimeout(() => { toast.hidden = true; }, 7000);
  }

  function initReveals(root) {
    const items = [...root.querySelectorAll('[data-reveal]')];
    if (!items.length) return;

    root.classList.add('is-reveal-ready');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach((item, index) => {
      item.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
      observer.observe(item);
    });
  }

  function updateThemeCartCount(count) {
    const selectors = [
      '[data-cart-count]',
      '.cart-count',
      '.cart-count-bubble span[aria-hidden="true"]',
      '.header__icon--cart .cart-count-bubble span:first-child',
    ];
    document.querySelectorAll(selectors.join(',')).forEach((node) => {
      node.textContent = String(count);
      node.closest('.cart-count-bubble')?.classList.remove('hidden');
    });
  }

  function initAll(scope = document) {
    if (scope.matches?.(SELECTOR)) initSection(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initSection);
  }

  document.addEventListener('click', handleDelegatedClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
